import React, { useEffect, useState } from "react";
import {
    Modal,
    Box,
    TextField,
    Button,
    Typography,
    IconButton,
    Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomTextField } from "components";

interface AddPaymentModalProps {
    open: boolean;
    handleClose: () => void;
    handleSubmit: (data: PaymentDetails) => void;
    initialValues: PaymentDetails;
    mode: "add" | "edit";
}

interface PaymentDetails {
    routingNumber: string;
    accountNumber: string;
    confirmAccountNumber: string;
}

const AddAccountDetailModal: React.FC<AddPaymentModalProps> = ({
    open,
    handleClose,
    handleSubmit,
    initialValues,
    mode
}) => {
    const [routingNumber, setRoutingNumber] = useState(
        initialValues.routingNumber
    );
    const [accountNumber, setAccountNumber] = useState(
        initialValues.accountNumber
    );
    const [confirmAccountNumber, setConfirmAccountNumber] = useState(
        initialValues.confirmAccountNumber
    );
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (mode === "edit") {
            // Populate fields with initial values when in edit mode
            setRoutingNumber(initialValues.routingNumber);
            setAccountNumber(initialValues.accountNumber);
            setConfirmAccountNumber(initialValues.confirmAccountNumber);
        } else {
            // Clear fields when in add mode
            setRoutingNumber("");
            setAccountNumber("");
            setConfirmAccountNumber("");
        }
    }, [mode, initialValues]);


    const onSubmit = () => {
        if (accountNumber !== confirmAccountNumber) {
            setError("Account numbers do not match");
            return;
        }
        const data = {
            routingNumber,
            accountNumber,
            confirmAccountNumber,
        };
        handleSubmit(data);
        handleClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={styleSheet.boxContainer}>
                <Box display="flex" justifyContent="flex-end">
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box>
                    <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        textAlign={"center"}
                        flexDirection={"column"}
                    >
                        <Typography
                            fontWeight={{ xs: 700, md: 500 }}
                            fontSize={{ xs: 18, md: 28 }}
                            color={{ xs: "#313131", md: "#233F91" }}
                        >
                            {mode === "add" ? "Add Account Details" : "Update Account Details"}

                        </Typography>
                        <Typography
                            variant="body2"
                            gutterBottom
                            width={{ xs: "254px", md: "409px" }}
                            fontWeight={400}
                            fontSize={17}
                            color={"#313131"}
                        >
                            Add a US bank account that you’d like your funds to be transferred
                            to
                        </Typography>
                    </Box>
                    <Box
                        pt={{ xs: 2, md: 0 }}
                        display={"flex"}
                        overflow={"hidden"}
                        mx={"auto"}
                        alignItems={"center"}
                        flexDirection={"column"}
                        justifyContent={"center"}
                    >
                        <Box
                            p={{ xs: 0, md: 6 }}
                            display={"flex"}
                            mx={"auto"}
                            flexDirection={"column"}
                            justifyContent={"center"}
                        >
                            <Box
                                display={"flex"}
                                gap={1}
                                // ml={xs: "20px", md: "0px"}
                                // justifyContent={{ xs: "left", md: "start" }}
                                alignItems={"center"}
                            >
                                <img
                                    src="/assets/icons/guidanceBank.png"
                                    alt="guidanceBank"
                                    style={{
                                        width: "24px",
                                        height: "23px",
                                    }}
                                />
                                <Typography
                                    fontWeight={700}
                                    fontSize={15}
                                    color={"#313131"}
                                    lineHeight={"24px"}
                                >
                                    Bank Account
                                </Typography>
                            </Box>
                            <Grid
                                py={{ xs: 2, md: 3 }}
                                display={"flex"}
                                alignItems={{ xs: "start", md: "center" }}
                                flexDirection={"column"}
                            >
                                <Grid display={"flex"} flexDirection={"column"}>
                                    <label htmlFor="routingNumber" style={styleSheet.labelText}>
                                        Routing Number
                                    </label>
                                    <Box>
                                        <CustomTextField
                                            name="routingNumber"
                                            type="number"
                                            sx={{ width: { xs: "290px", md: "459px" } }}
                                            value={routingNumber}
                                            onChange={(e) => setRoutingNumber(e.target.value)}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box
                                py={{ xs: 0, md: 3 }}
                                display="flex"
                                alignItems={{ xs: "start", md: "center" }}
                                flexDirection={{ xs: "column", md: "row" }}
                                gap={{ xs: 2, md: 4 }}
                            >
                                <Grid display={"flex"} flexDirection={"column"}>
                                    <label htmlFor="accountNumber" style={styleSheet.labelText}>
                                        Account Number
                                    </label>
                                    <Box>
                                        <CustomTextField
                                            name="accountNumber"
                                            type="number"
                                            sx={{ width: { xs: "290px", md: "212px" } }}
                                            value={accountNumber}
                                            onChange={(e) => {
                                                setAccountNumber(e.target.value);
                                                setError(null);
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid display={"flex"} flexDirection={"column"}>
                                    <label
                                        htmlFor="confirmAccountNumber"
                                        style={styleSheet.labelText}
                                    >
                                        Confirm Account Number
                                    </label>
                                    <Box>
                                        <CustomTextField
                                            name="confirmAccountNumber"
                                            type="number"
                                            sx={{ width: { xs: "290px", md: "212px" } }}
                                            value={confirmAccountNumber}
                                            onChange={(e) => {
                                                setConfirmAccountNumber(e.target.value);
                                                setError(null);
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Box>
                            {error && (
                                <Typography color="error" textAlign="center">
                                    {error}
                                </Typography>
                            )}
                            <Box
                                width={{ xs: "237px", md: "459px" }}
                                display="flex"
                                gap={{ xs: 1, md: 3 }}
                                justifyContent="flex-end"
                                mt={6}
                            >
                                <Button onClick={handleClose} sx={styleSheet.cancelBtn}>
                                    Cancel
                                </Button>
                                <Button sx={styleSheet.saveBtn} onClick={onSubmit}>
                                    {mode === "add" ? "Save" : "Update"}

                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

const styleSheet = {
    labelText: {
        fontWeight: 400,
        fontSize: "17px",
        color: "#797979",
    },
    boxContainer: {
        position: "absolute",
        top: { xs: "356px", md: "50%" },
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "292px", md: "626px" },
        maxHeight: { xs: "100vh", md: "100vh" },
        overflow: "auto",
        bgcolor: "background.paper",
        boxShadow: 24,
        p: { xs: 4, md: 1 },
        borderRadius: "10px",
        justifyContent: "center",
    },
    cancelBtn: {
        ml: 2,
        bgcolor: "#EEEEEE",
        width: "74px",
        height: "48px",
        color: "#233f91",
        border: "1.5px solid #233f91",
        borderRadius: "15px",
    },
    saveBtn: {
        bgcolor: "#233f91",
        width: "83px",
        height: "48px",
        color: "#fff",
        borderRadius: "15px",
        "&:hover": {
            bgcolor: "#1565c0",
            color: "#fff",
        },
    },
};

export default AddAccountDetailModal;
